import { useQuery } from '@powersync/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  buildViewModel,
  compileViewQuery,
  readViewFilter,
  sortPresetOf,
  SORT_PRESETS,
  updateHighlightType,
  updateViewOptions,
  viewChoices,
  type CompletedDisplay,
  type ListRow,
  type SortKey,
  type ViewItem,
} from '@flow/domain';
import {
  queries,
  type ActiveHighlightRow,
  type CaptureDestinationRow,
  type FieldDefRow,
  type HighlightTypeRow,
  type LocalQuery,
  type WorkspaceSummary,
} from '@flow/sync';
import {
  ContextChips,
  DoneSection,
  HiddenDone,
  HighlightCard,
  ProgressBar,
  TaskList,
  ViewOptionsPanel,
  type TaskRowData,
  type TaskRowLabels,
} from '@flow/ui/components';

import { formatDay } from '../capture/model.js';
import { useToday } from '../capture/use-today.js';
import type { LocalDatabase } from '../db/powersync.js';
import { t } from '../i18n/es.js';
import { useSetViewHeader } from '../shell/AppLayout.jsx';
import { isTypingTarget } from '../shell/use-shortcuts.js';

import { useDetailHost } from './use-detail-host.jsx';

/**
 * A flow list view: ASAP (EJE-01..04, EJE-09, TAI-01..04; docs/04 §3 and §4, mockups 01 and 02),
 * Backlog and Algún día (EJE-05) and Seguimiento (SEG-01..03, docs/04 §5). Two index-bounded
 * watched queries compiled from the saved view, the view model computed in JS, and every action as
 * a domain command applied in one local transaction with its undo. What shows depends on the view's
 * data, not on its key (design D5 of f1-deferred-views): completed tasks and progress when the
 * filter includes completed tasks, TAI when the view has highlights, overdue rows when it has a
 * badge.
 */

interface ViewSettingsRow {
  id: string;
  key: string;
  name: string;
  filter: string;
  group_by: string | null;
  sort: string;
  options: string;
}

interface ChildRow {
  id: string;
  parent_id: string;
  status: string | null;
  kind: string;
  completed_at: string | null;
}

const json = <T,>(value: string | null | undefined, fallback: T): T => {
  try {
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
};

const useRows = <T,>(query: LocalQuery | null) => {
  const q = query ?? queries.none();
  return useQuery<T>(q.sql, q.params);
};

/** Stable labels: the memoised rows compare them by identity. */
const rowLabels: TaskRowLabels = {
  complete: t.asap.complete,
  reopen: t.asap.reopen,
  subtasks: t.asap.subtasks,
  toggleHighlight: t.asap.toggleHighlight,
  move: t.asap.move,
  open: t.detail.open,
};
const doneLabels = { ...rowLabels, title: t.asap.doneSection };

/** Capture prefix shown before a grouping's tags (`@ordenador`, `+Fontanero`; areas have none). */
const prefixOf = (groupBy: string | null) =>
  groupBy === 'tag:context' ? '@' : groupBy?.startsWith('field:') ? '+' : '';

/** Field of an overdue badge (`options.badge`): `<field> lte $today`. */
function badgeField(options: Record<string, unknown>): string | null {
  const badge = options.badge as { filter?: Record<string, unknown> } | undefined;
  const filter = badge?.filter;
  if (filter?.op !== 'lte' || typeof filter.field !== 'string') return null;
  const value = filter.value as { $today?: number } | undefined;
  return value?.$today === 0 ? filter.field : null;
}

/** Custom field keys (`field:<key>`) named by these groupings, sorts or badge. */
function fieldKeys(names: (string | null | undefined)[]): string[] {
  const keys = new Set<string>();
  for (const name of names) if (name?.startsWith('field:')) keys.add(name.slice('field:'.length));
  return [...keys].sort();
}

export function ListView({
  userId,
  local,
  viewKey,
  openTaskId = null,
  onOpenTask,
}: {
  userId: string;
  local: LocalDatabase;
  viewKey: string;
  /** Task shown in the detail panel (`?tarea=` of the route). */
  openTaskId?: string | null;
  onOpenTask?: (id: string | null) => void;
}) {
  const setHeader = useSetViewHeader();
  const today = useToday(userId);
  const { data: workspaces } = useRows<WorkspaceSummary>(queries.personalWorkspace());
  const workspaceId = workspaces[0]?.id ?? '';
  const { data: views } = useRows<ViewSettingsRow>(queries.viewSettings(userId, viewKey));
  const view = views[0];
  // Stages of tasks and of projects: the panel moves and converts both (f1-type-conversion).
  const { data: stages } = useRows<CaptureDestinationRow>(queries.workspaceStages(workspaceId));
  const { data: periods } = useRows<{ id: string; started_at: string }>(queries.openPeriod(userId));
  const period = periods[0];

  const { data: fieldDefs } = useRows<FieldDefRow>(queries.fieldDefs(workspaceId));
  const filter = useMemo(() => json<unknown>(view?.filter, null), [view?.filter]);
  const shape = useMemo(() => {
    try {
      return filter ? readViewFilter(filter) : null;
    } catch {
      return null;
    }
  }, [filter]);
  const options = useMemo(() => json<Record<string, unknown>>(view?.options, {}), [view?.options]);
  const choices = useMemo(() => viewChoices(viewKey), [viewKey]);
  const sort = useMemo(
    () => json<SortKey[]>(view?.sort, [...SORT_PRESETS[choices.sorts[0]!]]),
    [view?.sort, choices],
  );
  const groupBy = view?.group_by ?? null;
  const overdueField = useMemo(() => badgeField(options), [options]);
  const completed = (options.completed ?? {}) as {
    display?: CompletedDisplay;
    window?: 'period' | 'today' | 'always';
  };
  const display: CompletedDisplay = completed.display ?? 'section';
  const completedWindow = completed.window ?? 'period';
  const showProgress = (options.progress as { show?: boolean } | undefined)?.show ?? true;
  const highlightOptions = options.highlights as
    { enabled?: boolean; type_key?: string } | undefined;
  const highlightsEnabled = highlightOptions?.enabled ?? false;
  // The stage of the view, or every stage of its category (Seguimiento, D2).
  const viewStages = useMemo(
    () =>
      stages.filter(
        (row) =>
          row.kind_scope === 'task' &&
          (shape?.stageKey ? row.key === shape.stageKey : row.category === shape?.stageCategory),
      ),
    [stages, shape],
  );
  // The period matters to the completed tasks and the TAI; Backlog does without it.
  const needsPeriod = Boolean(shape?.completedInPeriod) || highlightOptions !== undefined;
  // Fields of the view's own grouping (the person, even when not grouping), its order and badge.
  const fieldsKey = useMemo(
    () => fieldKeys([choices.groupBy, ...sort.map((key) => key.field), overdueField]).join(','),
    [choices, sort, overdueField],
  );
  const fields = useMemo(() => {
    const out: { key: string; id: string; type: 'date' | 'ref' }[] = [];
    for (const key of fieldsKey ? fieldsKey.split(',') : []) {
      const def = fieldDefs.find((row) => row.key === key);
      if (!def) return null;
      out.push({ key, id: def.id, type: def.type === 'date' ? 'date' : 'ref' });
    }
    return out;
  }, [fieldsKey, fieldDefs]);
  const taxonomyKey = groupBy?.startsWith('tag:')
    ? groupBy.slice('tag:'.length)
    : choices.groupBy.startsWith('tag:')
      ? choices.groupBy.slice('tag:'.length)
      : null;

  const compiled = useMemo(() => {
    if (!filter || viewStages.length === 0 || !fields || (needsPeriod && !period)) return null;
    // Start of today in this browser's zone (the user's zone in practice).
    const todayStart = new Date(`${today}T00:00:00`).toISOString();
    return compileViewQuery({
      filter,
      stage_ids: viewStages.map((row) => row.id),
      user_id: userId,
      today,
      period_start: period?.started_at ?? todayStart,
      today_start: todayStart,
      window: completedWindow,
      group_taxonomy_key: taxonomyKey,
      fields,
    });
  }, [
    filter,
    viewStages,
    fields,
    needsPeriod,
    period,
    userId,
    today,
    completedWindow,
    taxonomyKey,
  ]);
  const { data: openRows } = useRows<ListRow>(compiled?.open ?? null);
  const { data: doneRows } = useRows<ListRow>(compiled?.done ?? null);
  const rows = useMemo(() => {
    const byId = new Map<string, ListRow>();
    for (const row of [...openRows, ...doneRows]) byId.set(row.id, row);
    return [...byId.values()];
  }, [openRows, doneRows]);

  const { data: children } = useRows<ChildRow>(queries.childrenOf(rows.map((row) => row.id)));
  const { data: types } = useRows<HighlightTypeRow>(
    highlightOptions
      ? queries.highlightTypeByKey(userId, highlightOptions.type_key ?? 'tai')
      : null,
  );
  const type = types[0];
  const { data: active } = useRows<ActiveHighlightRow>(
    type && period ? queries.activeHighlights(userId, period.id, type.id) : null,
  );

  const [selected, setSelected] = useState<(string | null)[]>([]);
  const [focusedId, setFocusedState] = useState<string | null>(null);
  // The keys read the focused row synchronously: J then H must act on the row J just focused.
  const focusedRef = useRef<string | null>(null);
  const setFocusedId = (id: string | null) => {
    focusedRef.current = id;
    setFocusedState(id);
  };
  const [completedHere, setCompletedHere] = useState<Set<string>>(() => new Set());
  const [revealHidden, setRevealHidden] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);

  const highlighted = useMemo(() => new Set(active.map((h) => h.block_id)), [active]);
  const model = useMemo(
    () =>
      buildViewModel({
        rows,
        groupBy,
        sort,
        display,
        revealHidden,
        selected,
        highlighted: highlightsEnabled ? highlighted : new Set(),
        pinTop: highlightsEnabled && type?.pin_top === 1,
        completedHere,
        overdue: overdueField ? { field: overdueField, today } : undefined,
      }),
    [
      rows,
      groupBy,
      sort,
      display,
      revealHidden,
      selected,
      highlighted,
      highlightsEnabled,
      type?.pin_top,
      completedHere,
      overdueField,
      today,
    ],
  );

  // Header: view name, start of the period and the options button (mockup 01).
  const optionsButton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!view) return;
    setHeader({
      title: view.name,
      // The period only matters to the views that show its completed tasks (ASAP).
      ...(period && shape?.completedInPeriod
        ? { subtitle: t.asap.since(formatDay(period.started_at.slice(0, 10), today)) }
        : {}),
      actions: (
        <button
          ref={optionsButton}
          type="button"
          className="list-view__options"
          onClick={() => setOptionsOpen(true)}
        >
          {t.asap.options}
        </button>
      ),
    });
  }, [view, period, shape?.completedInPeriod, today, setHeader]);

  const childrenOf = (id: string) => children.filter((child) => child.parent_id === id);
  const rowById = (id: string) => rows.find((row) => row.id === id);

  // Complete, TAI, «Mover a…», convert and trash are shared with the detail panel (D7 of
  // f1-detail-panel), which lives in the shell's slot.
  const actions = useDetailHost({
    userId,
    local,
    workspaceId,
    stages,
    period,
    type,
    active,
    highlightsEnabled,
    today,
    openTaskId,
    onOpenTask,
    listBlock: rowById,
    listChildren: childrenOf,
    onAct: setFocusedId,
    onCompletedHere: (id, completed) =>
      setCompletedHere((set) => {
        const next = new Set(set);
        if (completed) next.add(id);
        else next.delete(id);
        return next;
      }),
    onClosed: () => {
      // Back to the row it was opened from (the keys read it from the ref).
      const id = focusedRef.current;
      if (id) document.querySelector<HTMLElement>(`[data-task-id="${id}"]`)?.focus();
    },
  });

  // Visible order of the rows, for J / K.
  const order = useMemo(() => {
    const ids: string[] = [];
    const add = (items: ViewItem[]) => {
      for (const item of items) if (!ids.includes(item.id)) ids.push(item.id);
    };
    add(model.pinned);
    for (const group of model.groups) add(group.items);
    add(model.doneSection);
    return ids;
  }, [model]);

  const focusRow = (id: string) => {
    setFocusedId(id);
    document.querySelector<HTMLElement>(`[data-task-id="${id}"]`)?.focus();
  };

  // Keys of the list (docs/05 §6.1): J/K move, H completes, I marks TAI, M moves.
  const openTask = (id: string | null) => onOpenTask?.(id);
  const handlers = useRef({ order, actions, optionsOpen, focusRow, openTask });
  useEffect(() => {
    handlers.current = { order, actions, optionsOpen, focusRow, openTask };
  });
  // Stable row handlers: with 500 rows, a change re-renders only the rows that changed.
  const rowActions = useMemo(
    () => ({
      onToggle: (id: string) => void handlers.current.actions.toggle(id),
      onToggleHighlight: (id: string) => void handlers.current.actions.toggleHighlight(id),
      onMove: (id: string) => handlers.current.actions.openMove(id),
      onOpen: (id: string) => handlers.current.openTask(id),
      onFocusTask: (id: string) => setFocusedId(id),
    }),
    [],
  );
  const cardLabels = useMemo(
    () => ({
      ...rowLabels,
      title: t.asap.taiTitle.replace('TAI', types[0]?.key.toUpperCase() ?? 'TAI'),
    }),
    [types],
  );
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const h = handlers.current;
      // ⌘⌫ (Ctrl+Supr elsewhere) sends the focused row to the trash (docs/05 §6.1, f1-trash-search
      // D7); the focus goes on to the next row.
      const trashKey =
        (event.metaKey && event.key === 'Backspace') || (event.ctrlKey && event.key === 'Delete');
      if (trashKey && !h.actions.busy && !h.optionsOpen && !event.defaultPrevented) {
        const focused = focusedRef.current;
        if (!focused || isTypingTarget(event.target)) return;
        if (event.target instanceof Element && event.target.closest('.detail')) return;
        event.preventDefault();
        const index = h.order.indexOf(focused);
        const next = h.order[index + 1] ?? h.order[index - 1] ?? null;
        void h.actions.trash(focused);
        if (next) h.focusRow(next);
        return;
      }
      if (
        h.actions.busy ||
        h.optionsOpen ||
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey
      )
        return;
      if (isTypingTarget(event.target)) return;
      const key = event.key.toLowerCase();
      if (key === 'j' || key === 'k') {
        if (h.order.length === 0) return;
        event.preventDefault();
        const current = focusedRef.current;
        const index = current ? h.order.indexOf(current) : -1;
        const next = key === 'j' ? Math.min(h.order.length - 1, index + 1) : Math.max(0, index - 1);
        h.focusRow(h.order[next]!);
        return;
      }
      const focused = focusedRef.current;
      if (!focused) return;
      if (key === 'h') {
        event.preventDefault();
        void h.actions.toggle(focused);
      } else if (key === 'i') {
        event.preventDefault();
        void h.actions.toggleHighlight(focused);
      } else if (key === 'm') {
        event.preventDefault();
        h.actions.openMove(focused);
      } else if (event.key === 'Enter') {
        // Enter opens the detail of the focused row (docs/05 §6.1). With the focus inside the
        // panel, its own controls answer: the state of the panel may reach this handler late.
        if (event.target instanceof Element && event.target.closest('.detail')) return;
        event.preventDefault();
        h.openTask(focused);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!view || viewStages.length === 0 || (needsPeriod && !period)) return null;

  const grouping = groupBy ?? choices.groupBy;
  const noGroup = t.lists.noGroup(grouping);
  const prefix = prefixOf(grouping);
  const personField = choices.groupBy.startsWith('field:')
    ? choices.groupBy.slice('field:'.length)
    : null;
  const toRow = (item: ViewItem): TaskRowData => {
    const kids = childrenOf(item.id);
    const doneKids = kids.filter((kid) => kid.status === 'done').length;
    // Seguimiento shows the follow-up date instead of the due date, with the overdue mark.
    const dateField = overdueField?.startsWith('field:')
      ? item.fieldValues[overdueField.slice('field:'.length)]
      : item.due_on;
    const person = personField ? item.fieldValues[`${personField}.name`] : null;
    const meta = [
      person ? `+${person}` : null,
      // Views that also list tasks visible later say from when (Backlog, Algún día).
      !shape?.visibleFromToday && item.start_on
        ? t.lists.visibleFrom(formatDay(item.start_on, today))
        : null,
    ]
      .filter(Boolean)
      .join(' · ');
    return {
      id: item.id,
      title: item.title,
      done: item.done,
      meta: meta || null,
      due: dateField ? formatDay(dateField, today) : null,
      dueUrgent: overdueField ? false : Boolean(item.due_on && item.due_on <= today && !item.done),
      subtasks: kids.length > 0 ? `${doneKids}/${kids.length}` : null,
      isHighlight: highlighted.has(item.id),
      overdue: item.overdue ? t.lists.overdue : null,
    };
  };
  const chipList = [
    ...model.chips,
    ...(model.hasUntagged ? [{ id: null, label: noGroup, count: model.untagged }] : []),
  ];
  const showsCompleted = Boolean(shape?.completedInPeriod);

  return (
    <div className="list-view" data-view={view.key}>
      {showsCompleted && showProgress ? (
        <ProgressBar
          done={model.progress.done}
          total={model.progress.total}
          name={t.asap.progress}
          label={t.asap.progressText}
        />
      ) : null}
      {groupBy ? (
        <ContextChips
          chips={chipList.map((chip) => ({
            ...chip,
            label: chip.id ? `${prefix}${chip.label}` : chip.label,
          }))}
          selected={selected}
          onSelectAll={() => setSelected([])}
          onToggle={(id) =>
            setSelected((current) =>
              current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
            )
          }
          labels={{ group: t.lists.chipsGroup(grouping), all: t.asap.all }}
        />
      ) : null}
      {actions.notice ? (
        <p className="list-view__notice" role="status">
          {actions.notice}
        </p>
      ) : null}
      {openRows.length >= 500 ? <p className="muted">{t.asap.capped(openRows.length)}</p> : null}
      {highlightsEnabled ? (
        <HighlightCard
          tasks={model.pinned.map(toRow)}
          usage={t.asap.taiUsage(active.length, type?.max_per_period ?? null)}
          focusedId={focusedId}
          onFocusTask={rowActions.onFocusTask}
          onToggle={rowActions.onToggle}
          onToggleHighlight={rowActions.onToggleHighlight}
          onMove={rowActions.onMove}
          labels={cardLabels}
        />
      ) : null}
      {model.groups.every((group) => group.items.length === 0) &&
      model.pinned.length === 0 &&
      model.doneSection.length === 0 ? (
        <p className="empty-state">{t.lists.empty(view.key)}</p>
      ) : (
        <TaskList
          label={t.asap.list}
          groups={model.groups.map((group) => ({
            key: group.key ?? 'none',
            label: group.key === null ? (groupBy ? noGroup : '') : `${prefix}${group.label}`,
            tasks: group.items.map(toRow),
          }))}
          focusedId={focusedId}
          onFocusTask={rowActions.onFocusTask}
          onToggle={rowActions.onToggle}
          {...(highlightsEnabled ? { onToggleHighlight: rowActions.onToggleHighlight } : {})}
          onMove={rowActions.onMove}
          onOpen={rowActions.onOpen}
          labels={rowLabels}
        />
      )}
      {showsCompleted ? (
        <DoneSection
          key={display}
          tasks={model.doneSection.map(toRow)}
          focusedId={focusedId}
          onFocusTask={rowActions.onFocusTask}
          onToggle={rowActions.onToggle}
          labels={doneLabels}
        />
      ) : null}
      {showsCompleted && display === 'hide' ? (
        <HiddenDone
          count={model.hiddenDone}
          onShow={() => setRevealHidden(true)}
          labels={{ hidden: t.asap.hidden, show: t.asap.show }}
        />
      ) : null}

      {actions.dialogs}

      {optionsOpen ? (
        <ViewOptionsPanel
          viewName={view.name}
          groupOptions={[
            { value: choices.groupBy, label: t.viewOptions.groups(choices.groupBy) },
            { value: 'none', label: t.viewOptions.noGrouping },
          ]}
          sortOptions={choices.sorts.map((key) => ({
            value: key,
            label: t.viewOptions.sorts[key],
          }))}
          value={{
            groupBy: groupBy ?? 'none',
            sort: sortPresetOf(sort) ?? choices.sorts[0]!,
            filterSummary: t.viewOptions.filterSummary(
              viewStages.map((row) => row.name).join(', '),
              Boolean(shape?.visibleFromToday),
            ),
            completed: showsCompleted ? display : null,
            window: completedWindow,
            progress: showProgress,
            highlights:
              highlightOptions && type
                ? {
                    enabled: highlightsEnabled,
                    max: type.max_per_period,
                    mode: type.limit_mode,
                    pinTop: type.pin_top === 1,
                  }
                : null,
          }}
          onGroupBy={(value) => {
            setSelected([]);
            void actions.run(viewPatch({ group_by: value === 'none' ? null : value }));
          }}
          onSort={(value) => {
            const preset = choices.sorts.find((key) => key === value);
            if (preset) void actions.run(viewPatch({ sort: preset }));
          }}
          onCompleted={(value) => {
            setRevealHidden(false);
            void actions.run(viewPatch({ completed: { display: value } }));
          }}
          onWindow={(value) => void actions.run(viewPatch({ completed: { window: value } }))}
          onProgress={(value) => void actions.run(viewPatch({ progress: { show: value } }))}
          onHighlightsEnabled={(value) =>
            void actions.run(viewPatch({ highlights_enabled: value }))
          }
          onMax={(value) => void actions.run(typePatch({ max_per_period: value }))}
          onMode={(value) => void actions.run(typePatch({ limit_mode: value }))}
          onPinTop={(value) => void actions.run(typePatch({ pin_top: value }))}
          onClose={() => setOptionsOpen(false)}
          labels={t.viewOptions}
        />
      ) : null}
    </div>
  );

  function viewPatch(patch: Parameters<typeof updateViewOptions>[0]['patch']) {
    return updateViewOptions({
      view: { id: view!.id, key: view!.key, group_by: view!.group_by, sort, options },
      patch,
    });
  }

  function typePatch(patch: Parameters<typeof updateHighlightType>[0]['patch']) {
    if (!type) return [];
    return updateHighlightType({
      type: {
        id: type.id,
        max_per_period: type.max_per_period,
        limit_mode: type.limit_mode,
        pin_top: type.pin_top === 1 ? 1 : 0,
      },
      patch,
    });
  }
}
